'use client'

import { useState, memo } from 'react'
import {
  UserIcon,
  FilmSlateIcon,
  FilmReelIcon,
  CalendarBlankIcon,
  EyeIcon,
  TagIcon,
} from '@phosphor-icons/react'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { ImageWithFallback } from '@/ui/image-with-fallback'
import { EditFormActions } from '@/feed/row-chrome'
import { DeleteButton } from '@/ui/delete-button'
import { EditButton } from '@/ui/edit-button'
import { SourceLink } from '@/feed/row-chrome'
import { MarkdownContent } from '@/ui/markdown-content'
import { formatRelativeTime } from '@/lib/date'
import {
  ExpandableRow,
  RowHeader,
  RowContent,
  RowActions,
  PeopleList,
  TopicBadges,
  MetaTagsRow,
  MetaChip,
} from '@/feed/row-chrome'
import type { RowProps } from '../types'
import type { MovieEntry } from './meta'
import type { MediaDraft } from './write-spec'
import type { MovieStatus } from './meta'

interface MovieMetadata {
  posterUrl?: string
  director?: string
  year?: number
  rating?: number
  overview?: string
  status?: string
}

type MovieRowProps = RowProps<MovieEntry, MediaDraft>

export const MovieRow = memo(
  function MovieRow({
    entry,
    isExpanded,
    onToggle,
    onDelete,
    onSave,
    isDeleting,
    isSaving,
  }: MovieRowProps) {
    const metadata = entry.metadata as MovieMetadata
    const people = entry.people || []
    const [isEditing, setIsEditing] = useState(false)
    const [editTitle, setEditTitle] = useState(entry.title)
    const [editDirector, setEditDirector] = useState(metadata.director || '')
    const [editYear, setEditYear] = useState(metadata.year?.toString() || '')
    const [editOverview, setEditOverview] = useState(metadata.overview || '')
    const [editTopics, setEditTopics] = useState(
      entry.topics?.map((t) => t.name).join(', ') || '',
    )
    const [editNotes, setEditNotes] = useState(entry.content || '')
    const [editStatus, setEditStatus] = useState<MovieStatus>(
      (metadata.status as MovieStatus) || 'want_to_watch',
    )

    const handleStartEdit = (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(true)
      setEditTitle(entry.title)
      setEditDirector(metadata.director || '')
      setEditYear(metadata.year?.toString() || '')
      setEditOverview(metadata.overview || '')
      setEditTopics(entry.topics?.map((t) => t.name).join(', ') || '')
      setEditNotes(entry.content || '')
      setEditStatus((metadata.status as MovieStatus) || 'want_to_watch')
    }

    const handleCancelEdit = () => {
      setIsEditing(false)
    }

    const handleSave = async () => {
      const topicsArray = editTopics
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean)
      await onSave({
        title: editTitle,
        director: editDirector || null,
        year: editYear ? parseInt(editYear, 10) : null,
        overview: editOverview || null,
        topics: topicsArray.length > 0 ? topicsArray : undefined,
        notes: editNotes || null,
        status: editStatus,
      })
      setIsEditing(false)
    }

    const header = (
      <RowHeader
        onToggle={onToggle}
        isExpanded={isExpanded}
        icon={
          <FilmSlateIcon
            className="text-entry-movie h-5 w-5 sm:h-4 sm:w-4"
            weight="duotone"
          />
        }
        title={entry.title}
        titleSuffix={
          entry.topics &&
          entry.topics.length > 0 && (
            <span className="hidden sm:inline">
              <TopicBadges topics={entry.topics} maxDisplay={2} />
            </span>
          )
        }
        metadata={
          <span className="text-muted-foreground/50 hidden text-xs sm:inline">
            {formatRelativeTime(entry.createdAt)}
          </span>
        }
      />
    )

    return (
      <ExpandableRow isExpanded={isExpanded} entryType="movie" header={header}>
        <RowContent
          showImage
          image={
            <ImageWithFallback
              src={entry.imageUrl}
              alt={entry.title}
              aspectRatio="portrait"
              fallbackIcon={FilmSlateIcon}
              fallbackColor="text-entry-movie"
            />
          }
        >
          {isEditing ? (
            <MovieEditForm
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              editDirector={editDirector}
              setEditDirector={setEditDirector}
              editYear={editYear}
              setEditYear={setEditYear}
              editOverview={editOverview}
              setEditOverview={setEditOverview}
              editTopics={editTopics}
              setEditTopics={setEditTopics}
              editStatus={editStatus}
              setEditStatus={setEditStatus}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onSave={handleSave}
            />
          ) : (
            <>
              {metadata.overview && (
                <p className="text-muted-foreground mb-2 line-clamp-2 text-sm sm:line-clamp-3">
                  {metadata.overview}
                </p>
              )}

              {entry.content && (
                <MarkdownContent className="font-body-serif text-foreground/60 mb-2 text-sm leading-relaxed italic">
                  {entry.content}
                </MarkdownContent>
              )}

              {/* Read-only meta chips */}
              <MetaTagsRow className="mb-2">
                {metadata.director && (
                  <MetaChip icon={<FilmReelIcon className="h-3 w-3" />}>
                    {metadata.director}
                  </MetaChip>
                )}
                {metadata.year && (
                  <MetaChip icon={<CalendarBlankIcon className="h-3 w-3" />}>
                    {metadata.year}
                  </MetaChip>
                )}
                {metadata.status && (
                  <MetaChip icon={<EyeIcon className="h-3 w-3" />}>
                    {metadata.status === 'seen' ? 'Seen' : 'Want to watch'}
                  </MetaChip>
                )}
                {entry.topics &&
                  entry.topics.length > 0 &&
                  entry.topics.map((topic) => (
                    <MetaChip
                      key={topic.id}
                      icon={<TagIcon className="h-3 w-3" />}
                    >
                      {topic.name}
                    </MetaChip>
                  ))}
              </MetaTagsRow>

              {people.length > 0 && (
                <div className="mb-2">
                  <PeopleList
                    people={people}
                    icon={<UserIcon className="h-3 w-3" />}
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <SourceLink entry={entry} />
                <div className="flex-1" />
                <RowActions>
                  <EditButton
                    onClick={handleStartEdit}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                  <DeleteButton
                    onClick={onDelete}
                    isDeleting={isDeleting}
                    variant="icon"
                    className="opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100"
                  />
                </RowActions>
              </div>
            </>
          )}
        </RowContent>
      </ExpandableRow>
    )
  },
  // Memoization comparison
  (prevProps, nextProps) => {
    return (
      prevProps.entry.id === nextProps.entry.id &&
      prevProps.entry.title === nextProps.entry.title &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.isDeleting === nextProps.isDeleting &&
      prevProps.isSaving === nextProps.isSaving &&
      prevProps.entry.url === nextProps.entry.url
    )
  },
)

interface MovieEditFormProps {
  editTitle: string
  setEditTitle: (v: string) => void
  editDirector: string
  setEditDirector: (v: string) => void
  editYear: string
  setEditYear: (v: string) => void
  editOverview: string
  setEditOverview: (v: string) => void
  editTopics: string
  setEditTopics: (v: string) => void
  editStatus: MovieStatus
  setEditStatus: (v: MovieStatus) => void
  editNotes: string
  setEditNotes: (v: string) => void
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

function MovieEditForm({
  editTitle,
  setEditTitle,
  editDirector,
  setEditDirector,
  editYear,
  setEditYear,
  editOverview,
  setEditOverview,
  editTopics,
  setEditTopics,
  editStatus,
  setEditStatus,
  editNotes,
  setEditNotes,
  isSaving,
  onCancel,
  onSave,
}: MovieEditFormProps) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Title
        </label>
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="h-8 text-sm"
          placeholder="Movie title"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">
            Director
          </label>
          <Input
            value={editDirector}
            onChange={(e) => setEditDirector(e.target.value)}
            className="h-8 text-sm"
            placeholder="Director name"
          />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">
            Year
          </label>
          <Input
            type="number"
            value={editYear}
            onChange={(e) => setEditYear(e.target.value)}
            className="h-8 text-sm"
            placeholder="2024"
          />
        </div>
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Overview
        </label>
        <Textarea
          value={editOverview}
          onChange={(e) => setEditOverview(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Movie description..."
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Topics (comma-separated)
        </label>
        <Input
          value={editTopics}
          onChange={(e) => setEditTopics(e.target.value)}
          className="h-8 text-sm"
          placeholder="Action, Drama, Thriller"
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Status
        </label>
        <select
          value={editStatus}
          onChange={(e) => setEditStatus(e.target.value as MovieStatus)}
          className="border-input bg-background text-foreground h-8 w-full rounded-md border px-2 text-sm"
        >
          <option value="want_to_watch">Want to watch</option>
          <option value="seen">Seen</option>
        </select>
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Notes
        </label>
        <Textarea
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Your personal notes..."
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
        disabled={!editTitle.trim()}
      />
    </div>
  )
}
